import api, {
  buildActivityScopeHeaders,
  buildCompanyScopeHeaders,
} from "./api";

const SUPPLY_CHAIN_BASE =
  "/supply-chain/suppliers";


function buildSupplierDocumentScopeConfig({
  companyUuid,
  activityUuid,
} = {}) {
  return {
    headers: {
      ...buildCompanyScopeHeaders(
        companyUuid,
      ),
      ...buildActivityScopeHeaders(
        activityUuid,
      ),
    },
  };
}

function requireSupplierUuid(value) {
  const normalized = String(
    value ?? ""
  ).trim();

  if (!normalized) {
    throw new TypeError(
      "supplierUuid is required"
    );
  }

  return normalized;
}

function supplierDocumentsPath(
  supplierUuid
) {
  return (
    `${SUPPLY_CHAIN_BASE}/` +
    `${encodeURIComponent(
      requireSupplierUuid(supplierUuid)
    )}/documents`
  );
}


function requireDocumentUuid(value) {
  const normalized = String(
    value ?? ""
  ).trim();

  if (!normalized) {
    throw new TypeError(
      "documentUuid is required"
    );
  }

  return normalized;
}


function supplierDocumentPath(
  supplierUuid,
  documentUuid
) {
  return (
    `${supplierDocumentsPath(
      supplierUuid
    )}/` +
    `${encodeURIComponent(
      requireDocumentUuid(
        documentUuid
      )
    )}`
  );
}

export async function listSupplierDocuments({
  supplierUuid,
  companyUuid,
  activityUuid,
}) {
  const response = await api.get(
    supplierDocumentsPath(
      supplierUuid
    ),
    buildSupplierDocumentScopeConfig({
      companyUuid,
      activityUuid,
    }),
  );

  const documents =
    response?.data?.documents;

  return Array.isArray(documents)
    ? documents
    : [];
}

export async function uploadSupplierDocument({
  supplierUuid,
  file,
  documentType,
  title = null,
  notes = null,
  metadata = null,
  companyUuid,
  activityUuid,
}) {
  if (!file) {
    throw new TypeError(
      "file is required"
    );
  }

  const normalizedDocumentType =
    String(
      documentType ?? ""
    ).trim();

  if (!normalizedDocumentType) {
    throw new TypeError(
      "documentType is required"
    );
  }

  const formData = new FormData();

  formData.append(
    "file",
    file
  );

  formData.append(
    "document_type",
    normalizedDocumentType
  );

  if (
    title !== null &&
    String(title).trim()
  ) {
    formData.append(
      "title",
      String(title).trim()
    );
  }

  if (
    notes !== null &&
    String(notes).trim()
  ) {
    formData.append(
      "notes",
      String(notes).trim()
    );
  }

  if (metadata !== null) {
    formData.append(
      "metadata",
      JSON.stringify(metadata)
    );
  }

  const response = await api.post(
    supplierDocumentsPath(
      supplierUuid
    ),
    formData,
    buildSupplierDocumentScopeConfig({
      companyUuid,
      activityUuid,
    }),
  );

  return response.data;
}

export async function downloadSupplierDocument({
  supplierUuid,
  documentUuid,
  companyUuid,
  activityUuid,
}) {
  const response = await api.get(
    `${supplierDocumentPath(
      supplierUuid,
      documentUuid
    )}/download`,
    {
      ...buildSupplierDocumentScopeConfig({
        companyUuid,
        activityUuid,
      }),
      responseType: "blob",
    },
  );

  return response;
}


export async function updateSupplierDocument({
  supplierUuid,
  documentUuid,
  documentType,
  title,
  notes,
  isActive,
  companyUuid,
  activityUuid,
}) {
  const payload = {};

  if (documentType !== undefined) {
    payload.document_type =
      documentType;
  }

  if (title !== undefined) {
    payload.title = title;
  }

  if (notes !== undefined) {
    payload.notes = notes;
  }

  if (isActive !== undefined) {
    payload.is_active = isActive;
  }

  const response = await api.patch(
    supplierDocumentPath(
      supplierUuid,
      documentUuid
    ),
    payload,
    buildSupplierDocumentScopeConfig({
      companyUuid,
      activityUuid,
    }),
  );

  return (
    response?.data?.supplier_document
    ?? response?.data
  );
}


export async function archiveSupplierDocument({
  supplierUuid,
  documentUuid,
  companyUuid,
  activityUuid,
}) {
  await api.delete(
    supplierDocumentPath(
      supplierUuid,
      documentUuid
    ),
    buildSupplierDocumentScopeConfig({
      companyUuid,
      activityUuid,
    }),
  );
}


export default {
  listSupplierDocuments,
  uploadSupplierDocument,
  downloadSupplierDocument,
  updateSupplierDocument,
  archiveSupplierDocument,
};
